// Shared client-side PDF export pipeline for the admin document/reference
// generators. html2canvas mis-renders inline <svg> text (embedded fonts +
// viewBox) inside an iframe, so every SVG is pre-rasterised to a PNG via the
// browser's own renderer before capture.

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const OXBLOOD = [122, 46, 46];

// Replace every <svg> in `root` with a PNG rendered by the browser (honours the
// SVG's embedded @font-face + viewBox). Images use inline-block so parent
// text-align:center keeps them centred.
export async function rasterizeSvgs(doc, root) {
  const svgs = Array.from(root.querySelectorAll("svg"));
  for (const svg of svgs) {
    const rect = svg.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));
    if (!clone.getAttribute("xmlns")) clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const str = new XMLSerializer().serializeToString(clone);
    const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(str);
    const im = new Image();
    im.width = w;
    im.height = h;
    await new Promise((res) => {
      im.onload = res;
      im.onerror = res;
      im.src = url;
    });
    const c = document.createElement("canvas");
    c.width = w * 3;
    c.height = h * 3;
    c.getContext("2d").drawImage(im, 0, 0, c.width, c.height);
    const out = doc.createElement("img");
    out.src = c.toDataURL("image/png");
    out.style.cssText = `display:inline-block;width:${w}px;height:${h}px;vertical-align:top;`;
    svg.parentNode.replaceChild(out, svg);
  }
  await new Promise((r) => setTimeout(r, 60));
}

function makeRowChecker(canvas, bg) {
  const W = canvas.width;
  const H = canvas.height;
  const data = canvas.getContext("2d").getImageData(0, 0, W, H).data;
  const x0 = Math.floor(W * 0.08);
  const x1 = Math.floor(W * 0.92);
  const tol = 12;
  return (y) => {
    if (y < 0 || y >= H) return true;
    for (let x = x0; x < x1; x += 3) {
      const i = (y * W + x) * 4;
      if (Math.abs(data[i] - bg[0]) > tol || Math.abs(data[i + 1] - bg[1]) > tol || Math.abs(data[i + 2] - bg[2]) > tol) return false;
    }
    return true;
  };
}

// Paginated A4 PDF with the brand's oxblood border on white, breaking at blank
// rows. `links` are clickable annotations in canvas-px: {x,y,w,h,url}.
export function buildBorderedPdf(canvas, filename, links = []) {
  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const pageMargin = 26;
  const contentPad = 22;
  const borderW = pageW - pageMargin * 2;
  const borderH = pageH - pageMargin * 2;
  const innerX = pageMargin + contentPad;
  const innerY = pageMargin + contentPad;
  const innerW = pageW - innerX * 2;
  const innerHpt = pageH - innerY * 2;
  const ratio = innerW / canvas.width;
  const usableHpx = Math.floor(innerHpt / ratio);
  const isBlank = makeRowChecker(canvas, [255, 255, 255]);
  const H = canvas.height;
  const W = canvas.width;

  const drawChrome = () => {
    pdf.setFillColor(255, 255, 255);
    pdf.rect(pageMargin, pageMargin, borderW, borderH, "F");
    pdf.setDrawColor(...OXBLOOD);
    pdf.setLineWidth(1.4);
    pdf.rect(pageMargin, pageMargin, borderW, borderH, "S");
  };

  let cursor = 0;
  let first = true;
  while (cursor < H - 2) {
    const target = Math.min(cursor + usableHpx, H);
    let breakY = target;
    if (target < H) {
      const floor = cursor + Math.floor(usableHpx * 0.35);
      for (let y = target; y > floor; y--) {
        if (isBlank(y)) { breakY = y; break; }
      }
    }
    const sliceH = breakY - cursor;
    if (sliceH <= 0) break;
    const tmp = document.createElement("canvas");
    tmp.width = W;
    tmp.height = sliceH;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, W, sliceH);
    tctx.drawImage(canvas, 0, cursor, W, sliceH, 0, 0, W, sliceH);
    if (!first) pdf.addPage();
    first = false;
    drawChrome();
    pdf.addImage(tmp.toDataURL("image/jpeg", 0.95), "JPEG", innerX, innerY, innerW, sliceH * ratio);
    for (const ln of links) {
      if (ln.y >= cursor && ln.y < breakY) {
        pdf.link(innerX + ln.x * ratio, innerY + (ln.y - cursor) * ratio, ln.w * ratio, ln.h * ratio, { url: ln.url });
      }
    }
    cursor = breakY;
    while (cursor < H && isBlank(cursor)) cursor++;
  }
  pdf.save(filename);
}

// Swap remote <img> sources for same-origin data URLs (via the backend image
// proxy) so html2canvas can render them without tainting the canvas.
async function inlineImages(root) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute("src") || "";
      if (!src || src.startsWith("data:")) return;
      try {
        const proxied = `${backend}/api/proxy-image?url=${encodeURIComponent(src)}`;
        const resp = await fetch(proxied);
        const blob = await resp.blob();
        const dataUrl = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        img.setAttribute("src", dataUrl);
        img.removeAttribute("loading");
        img.removeAttribute("crossorigin");
        await img.decode().catch(() => {});
      } catch (e) {
        /* leave original src on failure */
      }
    })
  );
}

// Export a combined "prospectus + writing sample" document (rendered in a hidden
// iframe) as a single bordered A4 PDF. Inlines remote images, rasterises SVGs,
// then paginates via the shared bordered-PDF builder.
export async function exportCombinedPdf({ iframe, filename }) {
  const doc = iframe && iframe.contentDocument;
  const liveSheet = doc && doc.querySelector(".sheet");
  if (!liveSheet) return;
  await (doc.fonts ? doc.fonts.ready : Promise.resolve());
  const liveW = liveSheet.getBoundingClientRect().width || 720;
  const clone = liveSheet.cloneNode(true);
  clone.style.cssText += `position:absolute;left:-10000px;top:0;width:${liveW}px;margin:0;background:#ffffff;`;
  doc.body.appendChild(clone);
  try {
    await inlineImages(clone);
    await new Promise((r) => setTimeout(r, 60));
    await rasterizeSvgs(doc, clone);
    const canvas = await html2canvas(clone, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: liveW,
    });
    const sheetRect = clone.getBoundingClientRect();
    const pxPerCss = canvas.width / sheetRect.width;
    const links = Array.from(clone.querySelectorAll("a[href]")).map((a) => {
      const r = a.getBoundingClientRect();
      return {
        x: (r.left - sheetRect.left) * pxPerCss,
        y: (r.top - sheetRect.top) * pxPerCss,
        w: r.width * pxPerCss,
        h: r.height * pxPerCss,
        url: a.href,
      };
    });
    buildBorderedPdf(canvas, filename, links);
  } finally {
    if (clone.parentNode) clone.parentNode.removeChild(clone);
  }
}

function parseRgb(str) {
  const m = (str || "").match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  return m ? [+m[1], +m[2], +m[3]] : [255, 255, 255];
}

// Plain paginated A4 PDF (no decorative border) that preserves the document's
// own background colour. Used for the internal reference documents.
function buildPlainPdf(canvas, filename, bg, links = []) {
  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const innerW = pageW - margin * 2;
  const ratio = innerW / canvas.width;
  const usableHpx = Math.floor((pageH - margin * 2) / ratio);
  const isBlank = makeRowChecker(canvas, bg);
  const H = canvas.height;
  const W = canvas.width;
  const bgCss = `rgb(${bg[0]},${bg[1]},${bg[2]})`;

  let cursor = 0;
  let first = true;
  while (cursor < H - 2) {
    const target = Math.min(cursor + usableHpx, H);
    let breakY = target;
    if (target < H) {
      const floor = cursor + Math.floor(usableHpx * 0.35);
      for (let y = target; y > floor; y--) {
        if (isBlank(y)) { breakY = y; break; }
      }
    }
    const sliceH = breakY - cursor;
    if (sliceH <= 0) break;
    const tmp = document.createElement("canvas");
    tmp.width = W;
    tmp.height = sliceH;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = bgCss;
    tctx.fillRect(0, 0, W, sliceH);
    tctx.drawImage(canvas, 0, cursor, W, sliceH, 0, 0, W, sliceH);
    if (!first) pdf.addPage();
    first = false;
    pdf.setFillColor(...bg);
    pdf.rect(0, 0, pageW, pageH, "F");
    pdf.addImage(tmp.toDataURL("image/jpeg", 0.95), "JPEG", margin, margin, innerW, sliceH * ratio);
    for (const ln of links) {
      if (ln.y >= cursor && ln.y < breakY) {
        pdf.link(margin + ln.x * ratio, margin + (ln.y - cursor) * ratio, ln.w * ratio, ln.h * ratio, { url: ln.url });
      }
    }
    cursor = breakY;
    while (cursor < H && isBlank(cursor)) cursor++;
  }
  pdf.save(filename);
}

// Export an internal reference document (Brand System, Brand Kit, Card Spec)
// rendered in an iframe to a background-aware, multi-page PDF, preserving the
// on-screen preview.
export async function exportReferencePdf({ iframe, filename }) {
  const doc = iframe && iframe.contentDocument;
  if (!doc || !doc.body) return;
  await (doc.fonts ? doc.fonts.ready : Promise.resolve());
  const src = doc.querySelector(".sheet") || doc.body;
  const view = doc.defaultView;
  let bgStr = view.getComputedStyle(src).backgroundColor;
  if (!bgStr || bgStr === "rgba(0, 0, 0, 0)" || bgStr === "transparent") {
    bgStr = view.getComputedStyle(doc.body).backgroundColor;
  }
  const bg = parseRgb(bgStr);
  const liveW = src.getBoundingClientRect().width;
  const clone = src.cloneNode(true);
  clone.style.cssText += `position:absolute;left:-10000px;top:0;width:${liveW}px;margin:0;`;
  doc.body.appendChild(clone);
  await new Promise((r) => setTimeout(r, 30));
  await rasterizeSvgs(doc, clone);
  const anchors = Array.from(clone.querySelectorAll("a[href]"));
  const canvas = await html2canvas(clone, {
    scale: 2,
    backgroundColor: `rgb(${bg[0]},${bg[1]},${bg[2]})`,
    useCORS: true,
    logging: false,
    windowWidth: liveW,
  });
  const sheetRect = clone.getBoundingClientRect();
  const pxPerCss = canvas.width / sheetRect.width;
  const linkRects = anchors.map((a) => {
    const r = a.getBoundingClientRect();
    return {
      x: (r.left - sheetRect.left) * pxPerCss,
      y: (r.top - sheetRect.top) * pxPerCss,
      w: r.width * pxPerCss,
      h: r.height * pxPerCss,
      url: a.href,
    };
  });
  if (clone.parentNode) clone.parentNode.removeChild(clone);
  buildPlainPdf(canvas, filename, bg, linkRects);
}
